<template>
  <div>
    <div class="upper-container">
      <div class="title-screen">
        <span>Floor plan</span>
      </div>
      <p class="page-description">
        To edit your seating layout, choose a floor plan from the list below or upload your venue
        floor plan.
      </p>
      <v-autocomplete
        v-model="selectedVenue"
        :items="venues"
        :label="'Venue'"
        item-text="name"
        return-object
        box
        :disabled="isAnyTicketAlreadySoldOrBooked"
        @change="initialVenue.id !== selectedVenue.id ? (showChangeVenueMessage = true) : ''"
      />
      <div>
        <div class="subtitle">
          <span>Edit floor plan</span>
        </div>
        <p class="page-description">
          Connect your tickets to seating areas. Click to select a specific seat, drag to select
          seats, then choose a ticket you want to connect.
        </p>
      </div>
    </div>
    <div class="lower-container">
      <div class="floor-plan">
        <div class="floor-plan-title">Floor plan</div>
        <div class="radio-buttons-container">
          <custom-radio-button
            label="Seats"
            :value="changeSelectOption === 1"
            @click="handleSelectOptions(1)"
          />
          <custom-radio-button
            label="Rows"
            :value="changeSelectOption === 2"
            @click="handleSelectOptions(2)"
          />
          <custom-radio-button
            label="Sections"
            :value="changeSelectOption === 3"
            @click="handleSelectOptions(3)"
          />
        </div>
        <div id="spinner-svg-wrapper">
          <div v-if="loadingSvg" id="spinner-container">
            <spinner />
          </div>
          <div style="position: relative">
            <object
              id="svgfile"
              :data="initialVenue.imageUrl"
              type="image/svg+xml"
              style="width: 100%"
            />
            <div id="svg-action-buttons-wrapper" style="display: none">
              <v-icon id="zoomIn" class="material-icons" :size="43" :color="zoomInIconColor">
                add_circle
              </v-icon>
              <v-icon id="zoomOut" class="material-icons" :size="43" :color="zoomOutIconColor">
                remove_circle
              </v-icon>
              <v-icon id="resetZoom" class="material-icons" :size="43" :color="resetZoomIconColor">
                replay_circle_filled
              </v-icon>
            </div>
          </div>
        </div>
        <floor-plan-stats
          class="floor-plan-stats"
          :all-order-items="[...regular, ...parter, ...table]"
          :selected-seats="selectedSeats"
          :init-venue-availability="initVenueAvailability"
          :venue-seat-ids="selectedVenue ? selectedVenue.seats : []"
        />
      </div>
      <div>
        <floor-plan-ticket-view
          ref="floor-plan-ticket-view"
          :init-regular="regular"
          :init-table="table"
          :init-parter="parter"
          :selected-ticket="selectedTicket"
          :svg-element-type="svgElementType"
          :selected-seats="selectedSeats"
          @handleConnectSeatWithTicket="
            selectedTicket = $event
            handleConnectSeatWithTicket()
          "
          @handleSeatAvailability="handleSeatAvailability($event)"
        />
        <div v-if="showChangeVenueMessage" class="change-venue-msg-container">
          <h1 class="change-venue-title">Change venue</h1>
          <p class="change-venue-description">
            Are you sure you want to change the venue? This will permanently delete your current
            venue configuration.
          </p>
          <div class="change-venue-buttons">
            <main-button text="Cancel" outline @click="setPreviousVenue()" />
            <main-button text="Change" color="#DB384C" @click="handleChangeVenue()" />
          </div>
        </div>
        <div v-if="showChangeVenueMessage" class="delete-message" @click="setPreviousVenue()" />
        <main-button
          style="margin-top: 36px"
          flat
          :disabled="isAnyTicketAlreadySoldOrBooked"
          @click="$emit('resetVenue')"
        >
          RESET
        </main-button>
      </div>
    </div>
    <simple-snackbar
      :open.sync="snackbarOpen"
      :message-prop="snackbarMessage"
      :color="snackbarColor"
    />
  </div>
</template>

<script>
import CustomRadioButton from '../events/custom-radio-button.vue'
import SimpleSnackbar from '../shared/snackbars/simple-snackbar.vue'
import {
  disconnectOrderItemSectionSeats,
  getVenueAvailability,
  updateOrderItemSectionSeats,
  updateVenueAvailability,
} from '@/http'
import * as svgPanZoom from 'svg-pan-zoom'
import * as Hammer from 'hammerjs'
import FloorPlanTicketView from '@/components/tickets/FloorPlanTicketView.vue'
import { SeatState, SvgElementCEType, Venue } from '@/types'
import FloorPlanStats from '@/components/tickets/FloorPlanStats.vue'

export default {
  name: 'create-floor-plan',
  components: {
    FloorPlanStats,
    FloorPlanTicketView,
    CustomRadioButton,
    SimpleSnackbar,
  },
  props: {
    eventId: String,
    isAnyTicketAlreadySoldOrBooked: Boolean,
    user: Object,
    organizationSlug: String,
    initRegular: {
      type: Array,
      default: () => [],
    },
    initParter: {
      type: Array,
      default: () => [],
    },
    initTable: {
      type: Array,
      default: () => [],
    },
    venues: {
      type: Array,
      default: () => [],
    },
    initVenueAvailability: {
      type: Object,
      default: () => {},
    },
    initialVenue: {
      type: Object,
      default: () => {},
    },
  },
  data() {
    return {
      snackbarOpen: false,
      snackbarMessage: '',
      snackbarColor: null,
      isMobile: /iPhone|iPad|iPod|Android/i.test(navigator.userAgent),
      venueAvailability: this.initVenueAvailability,
      changeSelectOption: 1,
      svgElementType: null,
      parter: this.initParter,
      table: this.initTable,
      selectedTicket: [],
      eventListenerType: { true: 'touchstart', false: 'click' },
      loadingSvg: true,
      zoomInIconColor: '#0277BD',
      zoomOutIconColor: '#0277BD',
      resetZoomIconColor: '#0277BD',
      selectedSections: [],
      prevZoom: null,
      selectedVenue: null,
      maxZoom: 3,
      venueSections: [],
      regular: this.initRegular,
      showChangeVenueMessage: false,
      selectedSeats: [],
      seatToTicketMap: {},
      seatToTypeMap: {},
      sectionToSeatsMap: {},
      clickStartX: null,
      clickStartY: null,
    }
  },
  mounted() {
    this.initialVenue && (this.selectedVenue = this.initialVenue) && this.handleInitialVenue()
  },
  methods: {
    handleSelectOptions(option) {
      this.handleReset()
      this.changeSelectOption = option
    },
    setPreviousVenue() {
      this.showChangeVenueMessage = false
      this.selectedVenue = this.initialVenue
    },
    getSectionColor: function (sectionId, svgDoc) {
      // Use first seatId from section for ce-type lookup
      const seatIds = this.sectionToSeatsMap[sectionId] || []
      const firstSeatId = seatIds[0]
      const type = this.getSvgElementType(svgDoc, firstSeatId)
      if (!type || !this[type]) return '#d0cccc'
      const tickets = this[type].filter(oi =>
        oi.sectionSeats.find(seat => seat.includes(sectionId)),
      )
      const sectionColor =
        (tickets.length && tickets.sort((a, b) => a.price - b.price)[0].color) || '#d0cccc'
    
      svgDoc.getElementById(sectionId).style.fill = sectionColor

      return sectionColor
    },
    canSelectSection: function (svgDoc, sectionId) {
      const previousSelectedSeatTicketIds = this.selectedTicket.map(st => st.id) || []
      const seatsHaveSameTickets =
        previousSelectedSeatTicketIds.sort().join(',') ===
        this[this.getSvgElementType(svgDoc, sectionId)]
          .filter(oi => oi.sectionSeats.some(seat => seat.includes(sectionId)))
          .map(oi => oi.id)
          .sort()
          .join(',')
      if (!seatsHaveSameTickets && this.selectedSeats.length) {
        this.snackbarColor = 'error'
        this.snackbarOpen = true
        this.snackbarMessage = 'Selected sections have different tickets!'

        return
      }
      this.selectedSeats = [
        ...this.selectedSeats,
        ...this.selectedVenue.predefinedSections[sectionId],
      ]
      this.selectedTicket = this[this.getSvgElementType(svgDoc, sectionId)].filter(
        oi =>
          oi.sectionSeats &&
          oi.sectionSeats.length &&
          oi.sectionSeats.some(seat => seat.includes(sectionId)),
      )
      this.selectedSections = [...this.selectedSections, sectionId]
      svgDoc.getElementById(sectionId).style.fill = 'white'
    },
    sectionClick(svgDoc) {
      this.venueSections.map(sectionId => {

        const sectionColor = this.getSectionColor(sectionId, svgDoc)

        svgDoc.getElementById(sectionId).addEventListener(
          this.eventListenerType[this.isMobile],
          function () {
            if (window.panZoom.getZoom() <= 1 && this.changeSelectOption === 3) {
              if (this.selectedSeats.some(s => s.includes(sectionId))) {
                svgDoc.getElementById(sectionId).style.fill = sectionColor
                this.selectedSeats = this.selectedSeats.filter(
                  seat => !this.selectedVenue.predefinedSections[sectionId].includes(seat),
                )
                this.selectedSections = this.selectedSections.filter(
                  section => section !== sectionId,
                )
                if (!this.selectedSections.length && !this.selectedSeats.length) {
                  this.selectedTicket = []
                }
              } else {
                this.canSelectSection(svgDoc, sectionId)
              }
            }
          }.bind(this),
        )
      })
      if (this.prevZoom <= 1) {
        this.selectedSections.map(
          sectionId => (svgDoc.getElementById(sectionId).style.fill = 'white'),
        )
      }

      return true
    },
    hideSeatsAtSpecificZoom(zoomLimit, currZoom, svgDoc) {
      if (this.selectedVenue.name === Venue.ARENA_ZAGREB) {
        if (currZoom <= zoomLimit) {
          svgDoc.getElementById('Seats_One_color').style.display = 'none'
          svgDoc.getElementById('Accessibility_seats').style.display = 'none'
          this.selectedSeats.map(seatId => this.colorSeat(seatId, svgDoc))
          this.sectionClick(svgDoc)
          this.prevZoom = window.panZoom.getZoom()
        } else {
          svgDoc.getElementById('Seats_One_color').style.display = 'unset'
          svgDoc.getElementById('Accessibility_seats').style.display = 'unset'
          this.venueSections.map(sectionId => {
            svgDoc.getElementById(sectionId).style.fill = 'transparent'
          })
          this.selectedSeats.map(seatId => {
            this.addWhiteColorOnElementSelect(seatId, svgDoc)
          })
          this.prevZoom = window.panZoom.getZoom()
        }
      }
    },

    getCurrZoom() {
      return window.panZoom.getZoom()
    },
    handleReset() {
      const svgDoc = document.getElementById('svgfile').getSVGDocument()
      this.selectedSeats.map(sr => this.colorSeat(sr, svgDoc))
      if (
        svgDoc !== null &&
        this.getCurrZoom() <= 1 &&
        this.selectedVenue.name === Venue.ARENA_ZAGREB
      ) {
        this.selectedSections.map(sectionId => {
          this.getSectionColor(sectionId, svgDoc)
        })
      }
      this.selectedSections = []
      this.selectedSeats = []
      this.emptyTicketsAndSvgElementType()
    },
    async handleConnectSeatWithTicket() {
      if (this.numberOfSeatsExceedsNumberOfTickets()) {
        this.snackbarColor = 'error'
        this.snackbarOpen = true
        this.snackbarMessage = this.$t('ticketQuantityError')

        return
      }
      const { data, errors } = await updateOrderItemSectionSeats({
        orderItemId: this.selectedTicket.id,
        seats: this.selectedSeats,
        organizationSlug: this.organizationSlug,
        venueId: this.selectedVenue.id,
        eventId: this.eventId,
      })
      if (errors) {
        this.snackbarColor = 'error'
        this.snackbarOpen = true
        this.snackbarMessage = errors[0].extensions.originalError.message
        this.$emit('ticketSoldInTheMeantime')

        return
      }
      if (data) {
        const updatedVenueAvailability = await getVenueAvailability({ eventId: this.eventId })
        this.venueAvailability = {
          ...this.venueAvailability,
          ...updatedVenueAvailability.data.getVenueAvailability.availability,
        }
      }
      const ticketType = this.svgElementType
      this[ticketType] = this[ticketType].map(oi => {
        if (oi.id === data.updateOrderItemSectionSeats.id) {
          return {
            ...oi,
            sectionSeats: [...data.updateOrderItemSectionSeats.sectionSeats],
          }
        } else {
          return oi
        }
      })
      const svgDoc = document.getElementById('svgfile').getSVGDocument()
      this.selectedSeats.map(seatId => this.colorSeat(seatId, svgDoc))
      this.buildLookupMaps(svgDoc)
      if (this.selectedVenue.name === 'ARENA_ZAGREB') {
        this.batchColorSeats(svgDoc, this.selectedVenue.seats, this.colorSeat)
      } else {
        this.selectedVenue.seats.forEach(seatId => this.colorSeat(seatId, svgDoc))
      }
      this.selectedSeats = []
      this.selectedTicket = []
      this.svgElementType = null
      this.handleReset()
      setTimeout(() => {
        this.$refs['floor-plan-ticket-view'].setCountPerTicket()
      }, 1000)
    },
    colorSeat(seatId, svgDoc) {
      svgDoc.getElementById(seatId).removeAttribute('stroke')
      const allTicketPerSeat = this.seatToTicketMap[seatId] || []
      if (allTicketPerSeat.length) {
        if (allTicketPerSeat.length > 1) {
          svgDoc.getElementById(seatId).style.fill = '#292929'
        } else {
          if (seatId.includes('Row') && seatId.includes('Seat')) {
            svgDoc.getElementById(seatId).style.fill =
              (allTicketPerSeat.length && allTicketPerSeat[0].color) || '#979797'
          } else {
            svgDoc.getElementById(seatId).style.fill = 'white'
          }
        }
      } else {
        svgDoc.getElementById(seatId).style.fill = '#d9d9d9'
      }
    },
    showDifferentTicketTypesErrorMsg() {
      this.snackbarColor = 'error'
      this.snackbarOpen = true
      this.snackbarMessage = 'Seats that are not selected have different tickets!'
    },
    showTicketSoldErrorMsg(errors) {
      this.snackbarColor = 'error'
      this.snackbarOpen = true
      this.snackbarMessage = errors[0].extensions.originalError.message

      this.$emit('ticketSoldInTheMeantime')
    },
    showDifferentElementTypesErrorMsg() {
      this.snackbarColor = 'error'
      this.snackbarOpen = true
      this.snackbarMessage = 'Elements that are not selected are different types!'
    },
    async handleSeatAvailability(state) {
      const svgDoc = document.getElementById('svgfile').getSVGDocument()
      const svgElementType = this.getSvgElementType(svgDoc, this.selectedSeats[0])
      if (svgElementType === SvgElementCEType.PARTER) {
        const { errors } = await disconnectOrderItemSectionSeats({
          eventId: this.eventId,
          seats: this.selectedSeats,
          organizationSlug: this.organizationSlug,
          venueId: this.selectedVenue.id,
        })
        if (errors) {
          return this.showTicketSoldErrorMsg(errors)
        }
      } else {
        const { data, errors } = await updateVenueAvailability({
          eventId: this.eventId,
          seats: this.selectedSeats,
          organizationSlug: this.organizationSlug,
          state: state,
          venueId: this.selectedVenue.id,
        })
        if (errors) {
          return this.showTicketSoldErrorMsg(errors)
        }
        this.venueAvailability = {
          ...this.venueAvailability,
          ...data.updateVenueAvailability.availability,
        }
      }
      this.handleUpdateOrderItemsAndSvgElements(svgDoc)
    },
    handleUpdateOrderItemsAndSvgElements(svgDoc) {
      const ticketType = this.getSvgElementType(svgDoc, this.selectedSeats[0])
      this[ticketType] = this[ticketType].map(oi => {
        const updatedSectionSeats = [...oi.sectionSeats]
        this.selectedSeats.map(ss => {
          oi.sectionSeats.includes(ss) &&
            updatedSectionSeats.splice(updatedSectionSeats.indexOf(ss), 1)
        })

        return {
          ...oi,
          sectionSeats: updatedSectionSeats,
        }
      })
      this.selectedSeats.map(ss => {
        const svgDoc = document.getElementById('svgfile').getSVGDocument()

        svgDoc.getElementById(ss).style.fill = '#D9D9D9'
        svgDoc.getElementById(`${ss}`).removeAttribute('stroke')
      })
      this.selectedSeats = []
      this.emptyTicketsAndSvgElementType()
      this.$refs['floor-plan-ticket-view'].resetCurrSelectedSeatState()
    },
    getSvgElementType(svgDoc, id) {
      return this.seatToTypeMap[id]
    },
    getSeatRow(s) {
      return s.split('_').find(seatRow => seatRow.includes('Row-'))
    },
    getSeatSection(s) {
      return s.split('_').find(seatRow => seatRow.includes('Section-'))
    },
    checkIfSeatsHaveSameTickets(svgDoc, s) {
      const previousSelectedSeatTicketIds = this.selectedTicket.map(st => st.id) || []

      return (
        previousSelectedSeatTicketIds.sort().join(',') ===
        this[this.getSvgElementType(svgDoc, s)]
          .filter(oi => oi.sectionSeats.includes(s))
          .map(oi => oi.id)
          .sort()
          .join(',')
      )
    },
    checkIfSectionsHaveSameTickets(svgDoc, s) {
      const previousSelectedSectionTicketIds = this.selectedTicket.map(st => st.id) || []

      return (
        previousSelectedSectionTicketIds.sort().join(',') ===
        this[this.getSvgElementType(svgDoc, s)]
          .filter(oi => oi.sectionSeats.includes(s))
          .map(oi => oi.id)
          .sort()
          .join(',')
      )
    },
    elementsAreSameType(svgDoc, next) {
      return this.svgElementType === next
    },
    setSelectedTickets(svgDoc, s) {
      const tickets = this.seatToTicketMap[s] || []
      this.selectedTicket = tickets

      return this.selectedTicket
    },
    emptyTicketsAndSvgElementType() {
      this.selectedTicket = []
      this.svgElementType = null
    },
    batchColorSeats(svgDoc, seatIds, colorFn, batchSize = 1000) {
      let i = 0
      const total = seatIds.length
      const processBatch = () => {
        const end = Math.min(i + batchSize, total)
        for (; i < end; i++) {
          const seatId = seatIds[i]
          colorFn.call(this, seatId, svgDoc)
        }
        if (i < total) {
          requestAnimationFrame(processBatch)
        }
      }
      processBatch()
    },
    initializeVenueMap(svgDoc) {
      if (this.selectedVenue.name === Venue.ARENA_ZAGREB) {
        this.batchColorSeats(svgDoc, this.selectedVenue.seats, this.colorSeat)
      } else {
        this.selectedVenue.seats.forEach(seatId => this.colorSeat(seatId, svgDoc))
      }
      this.selectedVenue.seats.forEach(seatId => {
        const initSeatState = this.venueAvailability && this.venueAvailability[seatId]
        if (
          (initSeatState && initSeatState.state === SeatState.SOLD) ||
          (initSeatState && initSeatState.state === SeatState.HOLD)
        ) {
          svgDoc.getElementById(seatId).style.fill = '#f5f5f5'
          svgDoc.getElementById(seatId).style.stroke = '#a1a0a0'
        } else {
          svgDoc.getElementById(seatId).style.fill =
            (initSeatState && initSeatState.state === SeatState.DISABLED && '#d9d9d9') || '#979797'
        }

        if (
          !initSeatState ||
          (initSeatState &&
            initSeatState.state !== SeatState.SOLD &&
            initSeatState.state !== SeatState.HOLD)
        ) {
          const svgElementType = this.getSvgElementType(svgDoc, seatId)
          const allTicketPerSeat = this[svgElementType].filter(
            oi => oi.sectionSeats && oi.sectionSeats.length && oi.sectionSeats.includes(seatId),
          )
          if (allTicketPerSeat.length) {
            if (allTicketPerSeat.length > 1) {
              svgDoc.getElementById(seatId).style.fill = '#292929'
            } else {
              svgDoc.getElementById(seatId).style.fill =
                (allTicketPerSeat.length && allTicketPerSeat[0].color) || '#979797'
            }
          }
        }
      })
      this.setZoomButtonsActions(svgDoc)
      svgDoc.getElementById('svg-pan-zoom-controls').remove()
    },
    setZoomButtonsActions(svgDoc) {
      document.getElementById('zoomIn').addEventListener(
        this.eventListenerType[this.isMobile],
        function () {
          window.panZoom.zoomIn()
          this.checkZoomButtonsState(true)
          this.hideSeatsAtSpecificZoom(1, this.getCurrZoom(), svgDoc)
        }.bind(this),
      )
      document.getElementById('zoomOut').addEventListener(
        this.eventListenerType[this.isMobile],
        function () {
          window.panZoom.zoomOut()
          this.checkZoomButtonsState(false)
          this.hideSeatsAtSpecificZoom(1, this.getCurrZoom(), svgDoc)
        }.bind(this),
      )
      document.getElementById('resetZoom').addEventListener(
        this.eventListenerType[this.isMobile],
        function () {
          window.panZoom.resetZoom()
          window.panZoom.center()
          this.hideSeatsAtSpecificZoom(1, this.getCurrZoom(), svgDoc)
          this.zoomInIconColor = '#0277BD'
          this.zoomOutIconColor = '#0277BD'
        }.bind(this),
      )
    },
    checkZoomButtonsState(isZoomingIn) {
      if (isZoomingIn) {
        if (window.panZoom.getZoom() >= this.maxZoom - 0.1) {
          this.zoomInIconColor = '#979797'
        }
        if (window.panZoom.getZoom() > 0.51) {
          this.zoomOutIconColor = '#0277BD'
        }
      } else {
        if (window.panZoom.getZoom() <= this.maxZoom - 0.1) {
          this.zoomInIconColor = '#0277BD'
        }
        if (window.panZoom.getZoom() <= 0.51) {
          this.zoomOutIconColor = '#979797'
        }
      }
    },
    numberOfSeatsExceedsNumberOfTickets() {
      const orderItem = this[this.svgElementType].find(oi => oi.id === this.selectedTicket.id)

      return this.selectedSeats.length + orderItem.sectionSeats.length > orderItem.quantity
    },
    handleChangeVenue() {
      if (this.selectedVenue.id !== this.initialVenue.id) {
        this.$emit('changeVenue', this.selectedVenue)
      }
    },
    handleInitialVenue() {
      const svgObject = document.getElementById('svgfile')
      if (svgObject) {
        svgObject.addEventListener(
          'load',
          () => {
            this.hammerInit()
            const svgDoc = svgObject.getSVGDocument()
            this.buildLookupMaps(svgDoc)
            this.initializeVenueMap(svgDoc)
            const svgRoot = svgDoc.documentElement
            svgRoot.addEventListener(this.isMobile ? 'touchstart' : 'mousedown', this.handleSvgDown)
            svgRoot.addEventListener(this.isMobile ? 'touchend' : 'mouseup', this.handleSvgClick)
            this.loadingSvg = false
            document.getElementById('spinner-svg-wrapper').style.height = 'fit-content'
            document.getElementById('svg-action-buttons-wrapper').style.display = 'flex'
          },
          { once: true },
        )
      }
      this.handleReset()
    },
    hammerInit: function () {
      let eventsHandler
      eventsHandler = {
        haltEventListeners: ['touchstart', 'touchend', 'touchmove', 'touchleave', 'touchcancel'],
        init: function (options) {
          let instance = options.instance,
            initialScale = 1,
            pannedX = 0,
            pannedY = 0

          // Init Hammer
          // Listen only for pointer and touch events
          this.hammer = Hammer(options.svgElement, {
            inputClass: Hammer.SUPPORT_POINTER_EVENTS
              ? Hammer.PointerEventInput
              : Hammer.TouchInput,
          })

          // Enable pinch
          this.hammer.get('pinch').set({
            enable: true,
          })

          // Handle double tap
          this.hammer.on('doubletap', function () {
            instance.zoomIn()
          })

          // Handle pan
          this.hammer.on('panstart panmove', function (ev) {
            // On pan start reset panned variables
            if (ev.type === 'panstart') {
              pannedX = 0
              pannedY = 0
            }

            // Pan only the difference
            instance.panBy({
              x: ev.deltaX - pannedX,
              y: ev.deltaY - pannedY,
            })
            pannedX = ev.deltaX
            pannedY = ev.deltaY
          })

          // Handle pinch
          this.hammer.on('pinchstart pinchmove', function (ev) {
            // On pinch start remember initial zoom
            if (ev.type === 'pinchstart') {
              initialScale = instance.getZoom()
              instance.zoomAtPoint(initialScale * ev.scale, {
                x: ev.center.x,
                y: ev.center.y,
              })
            }

            instance.zoomAtPoint(initialScale * ev.scale, {
              x: ev.center.x,
              y: ev.center.y,
            })
          })

          // Prevent moving the page on some devices when panning over SVG
          options.svgElement.addEventListener('touchmove', function (e) {
            e.preventDefault()
          })
        },
        destroy: function () {
          this.hammer.destroy()
        },
      }

      // Expose to window namespace for testing purposes
      window.panZoom = svgPanZoom('#svgfile', {
        zoomEnabled: true,
        controlIconsEnabled: true,
        maxZoom: this.selectedVenue.name === Venue.ARENA_ZAGREB ? 7 : 3,
        fit: 1,
        center: 1,
        customEventsHandler: eventsHandler,
      })
    },
    addWhiteColorOnElementSelect(seatId, svgDoc) {
      const selectedElement = svgDoc.getElementById(seatId)
      let strokeWidth = 2
      if (this.selectedVenue.name === Venue.ARENA_ZAGREB) {
        strokeWidth = 1
      } else if (svgDoc.getElementById(seatId).hasAttribute('r')) {
        strokeWidth = Number(svgDoc.getElementById(seatId).getAttribute('r')) * 0.4
      } else {
        strokeWidth = 2
      }
      selectedElement.style.fill = 'white'
      selectedElement.setAttribute('stroke', '#333333')
      selectedElement.setAttribute('stroke-width', strokeWidth)
    },
    handleSvgDown(e) {
      if (e.touches && e.touches.length) {
        this.clickStartX = e.touches[0].clientX
        this.clickStartY = e.touches[0].clientY
      } else {
        this.clickStartX = e.clientX
        this.clickStartY = e.clientY
      }
    },
    handleSvgClick(e) {
      let endX, endY
      if (e.changedTouches && e.changedTouches.length) {
        endX = e.changedTouches[0].clientX
        endY = e.changedTouches[0].clientY
      } else {
        endX = e.clientX
        endY = e.clientY
      }
      const delta = 6
      if (Math.abs(endX - this.clickStartX) > delta || Math.abs(endY - this.clickStartY) > delta) {
        return // drag/pan, ne klik
      }
      const svgDoc = e.currentTarget
      const s = e.target.id
      if (!s) return
      if (!svgDoc.getElementById(s)) return
      // Only proceed if the clicked element has a ce-type (is a seat)
      const svgElementType = this.seatToTypeMap[s]
      if (!svgElementType) return
      if (this.svgElementType === null) {
        this.svgElementType = svgElementType
      }
      const currSelectedSvgElementType = svgElementType
      if (this.changeSelectOption === 1) {
        if (this.selectedSeats.includes(s)) {
          this.setSelectedTickets(svgDoc, s)
          this.colorSeat(s, svgDoc)
          this.selectedSeats.splice(this.selectedSeats.indexOf(s), 1)
          this.emptyTicketsAndSvgElementType()
        } else {
          if (!this.elementsAreSameType(svgDoc, currSelectedSvgElementType)) {
            return this.showDifferentElementTypesErrorMsg()
          }
          if (!this.checkIfSeatsHaveSameTickets(svgDoc, s) && this.selectedSeats.length) {
            return this.showDifferentTicketTypesErrorMsg()
          }
          this.setSelectedTickets(svgDoc, s)
          this.addWhiteColorOnElementSelect(s, svgDoc)
          this.selectedSeats.push(s)
        }
      }
      if (this.changeSelectOption === 2) {
        const row = this.getSeatRow(s)
        const section = this.getSeatSection(s)
        const seatRemove = []
        if (!this.elementsAreSameType(svgDoc, currSelectedSvgElementType)) {
          return this.showDifferentElementTypesErrorMsg()
        }
        if (this.selectedSeats.includes(s)) {
          this.setSelectedTickets(svgDoc, s)
          this.selectedSeats = this.selectedSeats.filter(sel => {
            const seatRow = this.getSeatRow(sel)
            const seatSection = this.getSeatSection(sel)
            if (seatRow === row && seatSection === section) {
              seatRemove.push(sel)
            }

            return (seatSection !== section && seatRow === row) || seatRow !== row
          })
          seatRemove.map(sr => {
            this.colorSeat(sr, svgDoc)
          })
          this.emptyTicketsAndSvgElementType()
        } else {
          if (!this.checkIfSeatsHaveSameTickets(svgDoc, s) && !!this.selectedSeats.length) {
            return this.showDifferentTicketTypesErrorMsg()
          }
          this.setSelectedTickets(svgDoc, s)
          const selectedSeatTicketsIds = this.selectedTicket.map(st => st.id)
          let isThereTicketSeatDifferences = false
          const newSelectedSeats = [
            ...this.selectedVenue.seats.filter(sel => {
              const seatRow = this.getSeatRow(sel)
              const seatSection = this.getSeatSection(sel)
              if (seatRow === row && seatSection === section) {
                const currSeatTicketsIds = (this.seatToTicketMap[sel] || []).map(t => t.id)
                if (
                  selectedSeatTicketsIds.sort().join(',') !== currSeatTicketsIds.sort().join(',')
                ) {
                  isThereTicketSeatDifferences = true
                }

                return (
                  !this.venueAvailability ||
                  (seatRow === row &&
                    ((this.venueAvailability[sel] &&
                      this.venueAvailability[sel].state !== SeatState.SOLD &&
                      this.venueAvailability[sel].state !== SeatState.HOLD) ||
                      !this.venueAvailability[sel]) &&
                    selectedSeatTicketsIds.sort().join(',') === currSeatTicketsIds.sort().join(','))
                )
              }

              return false
            }),
          ]
          this.selectedSeats = [...this.selectedSeats, ...newSelectedSeats]
          if (isThereTicketSeatDifferences) {
            this.showDifferentTicketTypesErrorMsg()
          }
          newSelectedSeats.map(rs => {
            this.addWhiteColorOnElementSelect(rs, svgDoc)
          })
        }
      }
      if (this.changeSelectOption === 3) {
        const section = s.split('_').find(str => str.includes('Section'))
        if (!section) return
        if (this.selectedSeats.includes(s)) {
          this.setSelectedTickets(svgDoc, s)
          this.sectionToSeatsMap[section].forEach(seat => {
            if (this.selectedSeats.includes(seat)) {
              this.selectedSeats.splice(this.selectedSeats.indexOf(seat), 1)
              this.colorSeat(seat, svgDoc)
            }
          })
          this.selectedSections = this.selectedSections.filter(sec => sec !== section)
          this.emptyTicketsAndSvgElementType()
        } else {
          if (!this.elementsAreSameType(svgDoc, currSelectedSvgElementType)) {
            return this.showDifferentElementTypesErrorMsg()
          }
          if (!this.checkIfSectionsHaveSameTickets(svgDoc, s) && this.selectedSeats.length) {
            return this.showDifferentTicketTypesErrorMsg()
          }
          this.setSelectedTickets(svgDoc, s)
          let isThereTicketSeatDifferences = false
          const newSelectedSeats = [
            ...this.sectionToSeatsMap[section].filter(sel => {
              const currSeatTicketsIds = (this.seatToTicketMap[sel] || []).map(t => t.id)
              if (
                this.selectedTicket
                  .map(st => st.id)
                  .sort()
                  .join(',') !== currSeatTicketsIds.sort().join(',')
              ) {
                isThereTicketSeatDifferences = true
              }

              return (
                !this.venueAvailability ||
                (((this.venueAvailability[sel] &&
                  this.venueAvailability[sel].state !== SeatState.SOLD &&
                  this.venueAvailability[sel].state !== SeatState.HOLD) ||
                  !this.venueAvailability[sel]) &&
                  this.selectedTicket
                    .map(st => st.id)
                    .sort()
                    .join(',') === currSeatTicketsIds.sort().join(','))
              )
            }),
          ]
          this.selectedSeats = [...this.selectedSeats, ...newSelectedSeats]
          if (isThereTicketSeatDifferences) {
            this.showDifferentTicketTypesErrorMsg()
          }
          this.selectedSections = [...this.selectedSections, section]
          newSelectedSeats.map(seat => this.addWhiteColorOnElementSelect(seat, svgDoc))
        }
      }
    },
    buildLookupMaps(svgDoc) {
      // seatId -> ce-type
      this.seatToTypeMap = {}
      this.selectedVenue.seats.forEach(seatId => {
        const el = svgDoc.getElementById(seatId)
        if (el) {
          this.seatToTypeMap[seatId] = el.getAttribute('ce-type')
        }
      })
      // seatId -> [tickets] (svi ticketi koji sadrže seatId)
      this.seatToTicketMap = {}
      ;['regular', 'parter', 'table'].forEach(type => {
        this[type].forEach(oi => {
          oi.sectionSeats.forEach(seatId => {
            if (!this.seatToTicketMap[seatId]) this.seatToTicketMap[seatId] = []
            this.seatToTicketMap[seatId].push(oi)
          })
        })
      })
      // sectionId -> [seatIds]
      this.sectionToSeatsMap = {}
      if (this.selectedVenue.predefinedSections) {
        Object.entries(this.selectedVenue.predefinedSections).forEach(([sectionId, seatArr]) => {
          this.sectionToSeatsMap[sectionId] = seatArr
        })
      }
    },
  },
}
</script>

<style scoped>
p {
  margin-bottom: 0;
}

.page-description {
  font-family: 'Source Sans Pro';
  font-style: normal;
  font-weight: 400;
  font-size: 1rem;
  margin-bottom: 1.5rem;
  color: #979797;
}

.subtitle {
  font-family: 'Raleway';
  font-style: normal;
  font-weight: 600;
  font-size: 1.25rem;
  font-feature-settings: 'pnum' on, 'lnum' on;
  color: #666666;
  margin-top: 3rem;
  margin-bottom: 1rem;
}

.upper-container {
  max-width: 42.75rem;
}

.floor-plan {
  width: 42.75rem;
  height: fit-content;
  border: 2px solid #f5f5f5;
  border-radius: 8px;
}

.floor-plan-title {
  width: 100%;
  height: 3.5rem;
  background: #f5f5f5;
  font-family: 'Raleway';
  font-style: normal;
  font-weight: 600;
  font-size: 1.25rem;
  font-feature-settings: 'pnum' on, 'lnum' on;
  color: #666666;
  display: flex;
  align-items: center;
  text-indent: 1.5rem;
}

.radio-buttons-container {
  display: flex;
  margin: 16px 20px 16px;
}

.lower-container {
  display: flex;
  margin-top: 1.5rem;
  column-gap: 1.5rem;
}

@media screen and (max-width: 1331px) {
  .lower-container {
    flex-direction: column;
  }

  .floor-plan {
    width: unset;
    max-width: 42.75rem !important;
  }
}

@media screen and (max-width: 1012px) {
  .radio-buttons-container {
    flex-wrap: wrap;
    row-gap: 1rem;
  }
}

.change-venue-msg-container {
  width: 31.25rem;
  z-index: 1000;
  background: white;
  position: fixed;
  max-width: 80%;
  text-align: center;
  border-radius: 0.5rem;
  top: 25%;
  left: 50%;
  filter: drop-shadow(0px 0px 24px rgba(51, 51, 51, 0.2));
  padding: 1.25rem;
  transform: translate(-50%, 0);
}

.change-venue-title {
  font-family: 'Raleway';
  font-style: normal;
  font-weight: 600;
  font-size: 1.25rem;
  letter-spacing: -0.03em;
  color: #0277bd;
  margin-bottom: 1.5rem;
}

.change-venue-description {
  font-family: 'Source Sans Pro';
  font-style: normal;
  font-weight: 400;
  font-size: 1rem;
  color: #666666;
}

.change-venue-buttons {
  display: flex;
  justify-content: flex-end;
}

.delete-message {
  position: fixed;
  left: 0;
  top: 0;
  right: 0;
  bottom: 0;
  background: white;
  opacity: 80%;
  z-index: 500;
}

#svg-action-buttons-wrapper {
  display: flex;
  flex-direction: column;
  position: absolute;
  row-gap: 0.5rem;
  top: 20px;
  left: 21px;
}

.material-icons {
  user-select: none;
  cursor: pointer !important;
  background: white !important;
  height: fit-content;
  border-radius: 1000px !important;
  width: fit-content;
}

#spinner-container {
  height: 600px;
}

#spinner-svg-wrapper {
  height: 200px;
  overflow: hidden;
}

.floor-plan-stats {
  flex-wrap: wrap;
  gap: 8px;
}
</style>
